/* eslint-disable no-magic-numbers */
import { DateTime } from "luxon";

import {
	resolveVirtualMeetingType
} from "../../../domain";

import {
	luxonext
} from "@teaming/common-util";

import {
	formFactory,
	standardTouchForm
} from "@teaming/common-ui";

const debugNamespace = "teaming:common-team:stores:stream:recurrenceForm";

const translationFactory = i18n => ( key, args = {} ) => i18n.global.t( `common-team.stores.recurrenceForm.${ key }`, args );

const fields = [
	"dayOffsetField",
	"daysField",
	"documentName",
	"frequencyField",
	"linkField",
	"nameField",
	"meetingTemplateField",
	"meridiemField",
	"recurrenceId",
	"startDateField",
	"subDocId",
	"timeField",
	"timezoneField",
];

// eslint-disable-next-line no-unused-vars
const emptyRecurrenceForm = ( i18n, mergedOptions, mergedReadonlyData ) => {
	const { timezone, } = mergedReadonlyData;
	const now = DateTime.now().setZone( timezone );

	const emptyFields = fields.reduce( ( acc, field ) => {
		acc[ field ] = "";
		return acc;
	}, {} );

	const startOfNextHour = now.plus( { hours: 1, } )
		.startOf( "hour" );

	return {
		...emptyFields,
		dayOffsetField: 0, // no offset by default
		daysField: [ startOfNextHour.weekdayLong.toLowerCase(), ], // "monday"
		frequencyField: "weekx1", // once a week
		meridiemField: startOfNextHour.toFormat( "a" ).toLowerCase(), // "am"|"pm"
		startDateField: startOfNextHour.toFormat( "yyyy-MM-dd" ),
		timeField: startOfNextHour.toFormat( "hh:mm" ),
		timezoneField: timezone,
	};
};

const LUXON_WEEK_DAYS = Object.freeze( {
	monday: 1,
	tuesday: 2,
	wednesday: 3,
	thursday: 4,
	friday: 5,
	saturday: 6,
	sunday: 7,
} );

const PRIMEVUE_WEEK_DAYS = Object.freeze( {
	sunday: 0,
	monday: 1,
	tuesday: 2,
	wednesday: 3,
	thursday: 4,
	friday: 5,
	saturday: 6,
} );

const sortDays = days => {
	return days.sort( ( dayNameA, dayNameB ) => LUXON_WEEK_DAYS[ dayNameA ] - LUXON_WEEK_DAYS[ dayNameB ] );
};

// eslint-disable-next-line no-unused-vars,max-statements
export const validateRecurrenceForm = ( form, touches, i18n, mergedOptions, mergedReadonlyData ) => {
	const translate = translationFactory( i18n );
	const {
		dayOffsets,
		dayOffsetFrequencies,
		days,
		frequencies,
		meridiems,
		times,
		timezones,
	} = mergedReadonlyData;

	const errors = fields.reduce( ( acc, field ) => {
		acc[ field ] = "";
		return acc;
	}, {} );

	if ( touches.nameField ) {
		if ( !form.nameField.trim().length ) {
			errors.nameField = translate( "nameFieldMissing" );
		}
	}

	if ( touches.linkField ) {
		if ( !form.linkField.trim().length ) {
			errors.linkField = translate( "linkFieldMissing" );
		} else if ( !resolveVirtualMeetingType( form.linkField ) ) {
			errors.linkField = translate( "invalidLinkField" );
		}
	}

	if ( touches.frequencyField ) {
		if ( !form.frequencyField.trim().length ) {
			errors.frequencyField = translate( "frequencyFieldMissing" );
		} else if ( !frequencies.includes( form.frequencyField ) ) {
			errors.frequencyField = translate( "invalidFrequencyField" );
		}
	}

	if ( touches.dayOffsetField && dayOffsetFrequencies.includes( form.frequencyField ) ) {
		if ( !dayOffsets.includes( form.dayOffsetField ) ) {
			errors.dayOffsetField = translate( "invalidDayOffsetField" );
		}
	}

	if ( touches.daysField ) {
		if ( !form.daysField.length ) {
			errors.daysField = translate( "daysFieldMissing" );
		} else if ( !form.daysField.every( day => days.includes( day ) ) ) {
			errors.daysField = translate( "invalidDaysField" );
		}
	}

	if ( touches.timeField ) {
		if ( !form.timeField.trim().length ) {
			errors.timeField = translate( "timeFieldMissing" );
		} else if ( !times.includes( form.timeField ) ) {
			errors.timeField = translate( "invalidTimeField" );
		}
	}

	if ( touches.meridiemField ) {
		if ( !form.meridiemField.trim().length ) {
			errors.meridiemField = translate( "meridiemFieldMissing" );
		} else if ( !meridiems.includes( form.meridiemField ) ) {
			errors.meridiemField = translate( "invalidMeridiemField" );
		}
	}

	if ( touches.timezoneField ) {
		if ( !form.timezoneField.trim().length ) {
			errors.timezoneField = translate( "timezoneFieldMissing" );
		} else if ( !timezones.includes( form.timezoneField ) ) {
			errors.timezoneField = translate( "invalidTimezoneField" );
		}
	}

	if ( touches.startDateField ) {
		if ( !form.startDateField.trim().length ) {
			errors.startDateField = translate( "startDateFieldMissing" );
		}
	}

	const startDate = DateTime.fromFormat( form.startDateField, "yyyy-MM-dd", { zone: form.timezoneField, } );

	if ( form.frequencyField.startsWith( "week" ) ) {
		if ( !errors.startDateField ) {
			const { weekday, } = startDate;
			const matchedSelectedDay = form.daysField.find( day => LUXON_WEEK_DAYS[ day ] === weekday );
			if ( !matchedSelectedDay ) {
				errors.startDateField = translate( "incorrectStartDateDays", {
					days: sortDays( form.daysField )
						.map( weekdayName => translate( `${ weekdayName }LongLabel` ) )
						.join( ", " ),
				} );
			}
		}
	}

	if ( form.frequencyField.startsWith( "month" ) ) {
		if ( !errors.startDateField ) {
			const { month, year, } = startDate;
			const weekdayName = form.daysField[ 0 ];
			const weekdayNumber = LUXON_WEEK_DAYS[ weekdayName ];
			const { match: nthWeekday, } = luxonext.nthWeekdayOfMonth( {
				nth: form.dayOffsetField,
				weekday: weekdayNumber,
				month,
				year,
				timezone: form.timezoneField,
				withUnmatched: false,
			} );
			if ( startDate.day !== nthWeekday.day ) {
				errors.startDateField = translate( "incorrectStartDateDay", {
					nth: translate( `offset${ form.dayOffsetField }Label` ),
					day: translate( `${ weekdayName }LongLabel` ),
				} );
			}
		}
	}

	return errors;
};

const defaultOptions = {
	touchAll: false,
};

// eslint-disable-next-line new-cap
const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const timezones = Intl.supportedValuesOf( "timeZone" );

const twelveHours = Array( 12 ).fill( 0 )
	.map( ( _, i ) => i < 9 ? `0${ i + 1 }` : `${ i + 1 }` );

const hourInFiveMinIncrements = Array( 12 ).fill( 0 )
	.map( ( _, i ) => i < 2 ? `0${ i * 5 }` : `${ i * 5 }` );

// 12 hours in 5 min increments
const times = twelveHours.reduce( ( acc, hour ) => {
	return [
		...acc,
		...hourInFiveMinIncrements.map( min => `${ hour }:${ min }` ),
	];
}, [] );

const defaultReadonlyData = {
	dayOffsets: Object.freeze( [ 1, 2, 3, 4, ] ),
	dayOffsetFrequencies: Object.freeze( [
		"monthx1",
		"monthx2",
		"monthx3",
		"monthx6",
	] ),
	multiDayFrequencies: Object.freeze( [
		"weekx1",
	] ),
	days: Object.freeze( [
		"sunday",
		"monday",
		"tuesday",
		"wednesday",
		"thursday",
		"friday",
		"saturday",
	] ),
	frequencies: Object.freeze( [
		"weekx1",
		"weekx2",
		"weekx3",
		"weekx6",
		"monthx1",
		"monthx2",
		"monthx3",
		"monthx6",
	] ),
	meridiems: Object.freeze( [
		"am",
		"pm",
	] ),
	times,
	timezone,
	timezones,
};

// eslint-disable-next-line no-unused-vars
const computeRecurrenceCapabilities = ( form, mergedOptions, mergedReadonlyData ) => {
	const allowDayOffset = form.frequencyField.startsWith( "month" );
	const allowMultiDaySelection = ( form.frequencyField === "weekx1" );

	return {
		allowDayOffset,
		allowMultiDaySelection,
	};
};

const makeForm = formFactory( {
	emptyForm: emptyRecurrenceForm,
	touchForm: standardTouchForm( fields ),
	validateForm: validateRecurrenceForm,
	computeCapabilities: computeRecurrenceCapabilities,
	defaultOptions,
	defaultReadonlyData,
	debugNamespace,
} );

export default args => {
	const form = makeForm( args );

	form.daysField = sortDays( form.daysField );

	const disabledStartDays = Object.keys( PRIMEVUE_WEEK_DAYS ).reduce( ( acc, weekday ) => {
		if ( !form.daysField.includes( weekday ) ) {
			acc.push( PRIMEVUE_WEEK_DAYS[ weekday ] );
		}
		return acc;
	}, [] );

	const firstWeekdayName = form.daysField[ 0 ];
	const firstWeekdayNumber = LUXON_WEEK_DAYS[ firstWeekdayName ];

	const filteredStartDay = {
		ordinal: form.dayOffsetField,
		weekday: firstWeekdayNumber || 0,
	};

	form.disabledStartDays = disabledStartDays;
	form.filteredStartDay = filteredStartDay;

	return form;
};

export function recurrenceFromFields( form ) {
	const {
		recurrenceId: id,
		dayOffsetField: dayOffset,
		daysField: days,
		documentName,
		frequencyField: frequency,
		linkField: link,
		nameField: name,
		meetingTemplateField: meetingTemplateId,
		meridiemField: meridiem,
		startDateField: startDate,
		subDocId,
		timeField: time,
		timezoneField: _timezone,
	} = form;

	return {
		id,
		dayOffset,
		days,
		documentName,
		frequency,
		link,
		name,
		meetingTemplateId,
		meridiem,
		startDate,
		subDocId,
		time,
		timezone: _timezone,
	};
}

export function recurrenceToFields( recurrence ) {
	const {
		dayOffset: dayOffsetField,
		days: daysField,
		documentName,
		frequency: frequencyField,
		link: linkField,
		meetingTemplateId: meetingTemplateField,
		meridiem: meridiemField,
		name: nameField,
		id: recurrenceId,
		startDate: startDateField,
		subDocId,
		time: timeField,
		timezone: timeZoneField,
	} = recurrence;

	return {
		dayOffsetField,
		daysField,
		documentName,
		frequencyField,
		linkField,
		meetingTemplateField,
		meridiemField,
		nameField,
		recurrenceId,
		startDateField,
		subDocId,
		timeField,
		timeZoneField,
	};
}
